'use client';

import { useTranslation } from 'react-i18next';
import { useLocale, type MoneyValue } from '@akademi/site-builder-runtime';
import { formatMoney } from '@akademi/site-builder-blocks';

/**
 * Price display shared by {@code ProductGrid}, {@code ProductDetail} and
 * {@code KitEnquiryCta}. `listPrice` renders struck-through beside `price`
 * only when both are present AND differ — an absent `listPrice` means "no
 * discount", not "equal to price" (see {@link MoneyValue} / `ProductCard`
 * doc), so this never invents a was-price.
 */
export function ProductPrice({
  price,
  listPrice,
  size = 'md',
}: {
  price?: MoneyValue;
  listPrice?: MoneyValue;
  size?: 'sm' | 'md' | 'lg';
}) {
  const { t } = useTranslation('site-builder-blocks');
  const { locale } = useLocale();

  if (!price) {
    return (
      <span style={{ color: 'var(--color-text-tertiary, #71717a)' }}>
        {t('commerce.price.onRequest')}
      </span>
    );
  }

  const fontSize =
    size === 'lg'
      ? 'var(--font-size-2xl, 24px)'
      : size === 'sm'
        ? 'var(--font-size-sm, 14px)'
        : 'var(--font-size-lg, 18px)';
  const showWasPrice = Boolean(listPrice) && listPrice!.amount !== price.amount;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
      <span style={{ fontSize, fontWeight: 700, color: 'var(--color-text-primary, #18181b)' }}>
        {formatMoney(price, locale)}
      </span>
      {showWasPrice && (
        <span
          style={{
            fontSize: 'var(--font-size-sm, 13px)',
            color: 'var(--color-text-tertiary, #71717a)',
            textDecoration: 'line-through',
          }}
          aria-label={t('commerce.price.wasLabel', { price: formatMoney(listPrice!, locale) })}
        >
          {formatMoney(listPrice!, locale)}
        </span>
      )}
      {price.taxInclusive === false && (
        <span
          style={{
            fontSize: 'var(--font-size-xs, 12px)',
            color: 'var(--color-text-tertiary, #71717a)',
          }}
        >
          {t('commerce.price.exTax')}
        </span>
      )}
    </span>
  );
}
